import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import SectionHeading from "@/components/base/SectionHeading";
import { getActorByName, getMovieById, getRelatedMovies } from "@/utils/data";

export default function DetailPage() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();

  useSeo({
    title: movie
      ? `《${movie.title}》详情介绍及剧情解析 - 蜜桃成熟时1997影视内容站`
      : "影片详情 - 蜜桃成熟时1997影视内容站",
    description: movie
      ? `《${movie.title}》(${movie.year}) 详细介绍：导演${movie.director}，主演${movie.cast.join("、")}。提供剧情简介、深度解析与相关推荐。`
      : "经典影片详情介绍与剧情解析。",
    keywords: movie
      ? `${movie.title},${movie.title}剧情解析,${movie.director},${movie.cast.join(",")},经典电影`
      : undefined,
  });

  if (!movie) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <span className="mx-auto w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
          <i className="ri-file-unknow-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">
          未找到该影片
        </h1>
        <p className="mt-3 text-sm text-foreground-500">
          你访问的影片可能已被移除，去看看其他经典作品吧。
        </p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i>
          返回电影分类
        </Link>
      </div>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const favorited = isFavorite(movie.id);

  const infoItems = [
    { label: "导演", value: movie.director },
    { label: "类型", value: movie.genre },
    { label: "地区", value: movie.region },
    { label: "时长", value: movie.duration },
    { label: "上映日期", value: movie.releaseDate },
  ];

  return (
    <div className="w-full">
      <div className="w-full px-4 md:px-8 pt-8 md:pt-10">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "电影分类", to: "/category" },
            { label: movie.title },
          ]}
        />
      </div>

      <section className="w-full px-4 md:px-8 py-8 md:py-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <div className="w-full sm:w-[280px] lg:w-[320px] shrink-0">
            <div className="w-full aspect-[2/3] rounded-lg overflow-hidden border border-background-200 bg-background-200">
              <img
                src={movie.cover}
                alt={`${movie.title} 电影封面`}
                title={`${movie.title} - 影片详情`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <button
              type="button"
              onClick={() => toggleFavorite(movie.id)}
              className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                favorited
                  ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                  : "border border-background-300 text-foreground-800 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
              {favorited ? "已收藏" : "收藏这部影片"}
            </button>
            <p className="mt-2 text-xs text-foreground-400 text-center">
              收藏状态保存在本机浏览器，仅供演示
            </p>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
                {movie.title}
              </h1>
              <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-sm">
                {movie.year}
              </span>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5 text-accent-600 font-medium">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)} 分
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line"></i>
                {movie.region}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {movie.duration}
              </span>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 rounded-lg border border-background-200 bg-background-50 p-5">
              {infoItems.map((item) => (
                <div key={item.label} className="flex gap-3 text-sm">
                  <span className="text-foreground-500 w-16 shrink-0">{item.label}</span>
                  <span className="text-foreground-900">{item.value}</span>
                </div>
              ))}
              <div className="flex gap-3 text-sm sm:col-span-2">
                <span className="text-foreground-500 w-16 shrink-0">主演</span>
                <span className="flex flex-wrap gap-x-3 gap-y-1">
                  {movie.cast.map((name) => {
                    const actor = getActorByName(name);
                    return actor ? (
                      <Link
                        key={name}
                        to={`/actors/${actor.id}`}
                        className="text-primary-600 hover:text-primary-700 hover:underline"
                      >
                        {name}
                      </Link>
                    ) : (
                      <span key={name} className="text-foreground-700">
                        {name}
                      </span>
                    );
                  })}
                </span>
              </div>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">
                {movie.synopsis}
              </p>
            </div>

            <div className="mt-8">
              <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                深度剧情解析
              </h2>
              <div className="rounded-lg border-l-4 border-primary-400 bg-primary-50/60 p-5">
                <p className="text-sm md:text-base text-foreground-700 leading-loose">
                  {movie.plotAnalysis}
                </p>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-sm font-medium text-foreground-500 mb-3">所属分类</h3>
              <div className="flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category?tag=${encodeURIComponent(tag)}`}
                    className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-sm hover:bg-primary-100 hover:text-primary-700 transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full px-4 md:px-8 py-8 md:py-12 bg-background-100">
          <SectionHeading
            eyebrow="More Like This"
            title="相关推荐"
            description="基于题材与年代标签为你推荐的相似作品。"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}